import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Droplet, Heart, Activity, Plus, Zap, Clock, Smile, Wind, Moon, AlertCircle, TrendingUp, Sprout, Home, BarChart3, MessageCircle, BookOpen } from "lucide-react";
import "./CycleTrackTab.css";

import Dashboard   from "../../CycleBot/Dashboard/Dashboard";
import DataView    from "../../CycleBot/DataView/DataView";
import ChatView    from "../../CycleBot/ChatView/ChatView";
import FlowsView   from "../../CycleBot/FlowsView/FlowsView";
import JournalView from "../../CycleBot/JournalView/JournalView";

/* ── Inline Icons ── */
const DropletIcon = () => <Droplet size={22} />;
const HeartIcon = () => <Heart size={22} />;
const ActivityIcon = () => <Activity size={22} />;
const PlusIcon = () => <Plus size={22} />;

/* ── Symptom Icons ── */
function SymptomIcon({ type }) {
  switch (type) {
    case "cramps":
      return <Zap size={18} />;
    case "headache":
      return <Clock size={18} />;
    case "mood":
      return <Smile size={18} />;
    case "bloating":
      return <Wind size={18} />;
    case "fatigue":
      return <Moon size={18} />;
    case "breast":
      return <AlertCircle size={18} />;
    default:
      return null;
  }
}

/* ── Data ── */
const SYMPTOMS_CONFIG = [
  { key: "cramps",            translationKey: "cycleBot.trackTab.symptoms.cramps",            icon: "cramps",   activeColor: "#db2777", activeBg: "#fce7f3" },
  { key: "headache",          translationKey: "cycleBot.trackTab.symptoms.headache",          icon: "headache", activeColor: "#7c3aed", activeBg: "#f5f3ff" },
  { key: "mood_swings",       translationKey: "cycleBot.trackTab.symptoms.moodSwings",       icon: "mood",     activeColor: "#2563eb", activeBg: "#dbeafe" },
  { key: "bloating",          translationKey: "cycleBot.trackTab.symptoms.bloating",          icon: "bloating", activeColor: "#059669", activeBg: "#d1fae5" },
  { key: "fatigue",           translationKey: "cycleBot.trackTab.symptoms.fatigue",           icon: "fatigue",  activeColor: "#d97706", activeBg: "#fef3c7" },
  { key: "breast_tenderness", translationKey: "cycleBot.trackTab.symptoms.breastTenderness", icon: "breast",   activeColor: "#dc2626", activeBg: "#fee2e2" },
];

const STATIC_ASSESSMENTS = [
  { id: "irregularities", icon: <TrendingUp size={28} />, iconBg: "#dbeafe", iconColor: "#2563eb", title: "Cycle Irregularities",  description: "Evaluate cycle regularity and identify factors",       duration: "5-7 min", questions: 5 },
  { id: "pain",           icon: <Zap size={28} />, iconBg: "#fff7ed", iconColor: "#ea580c", title: "Pain Assessment",       description: "Characterize menstrual pain for better management",   duration: "4-6 min", questions: 5 },
  { id: "flow",           icon: <Droplet size={28} />, iconBg: "#fce7f3", iconColor: "#db2777", title: "Flow Analysis",         description: "Analyze bleeding patterns and duration",              duration: "4-5 min", questions: 5 },
  { id: "pms",            icon: <Moon size={28} />, iconBg: "#f5f3ff", iconColor: "#7c3aed", title: "PMS Evaluation",        description: "Identify premenstrual symptoms and intensity",         duration: "5-6 min", questions: 5 },
  { id: "fertility",      icon: <Sprout size={28} />, iconBg: "#ecfdf5", iconColor: "#059669", title: "Fertility Tracking",    description: "Understand your fertile window and ovulation",        duration: "5-7 min", questions: 7 },
  { id: "stress",         icon: <Wind size={28} />, iconBg: "#ecfeff", iconColor: "#0891b2", title: "Stress & Lifestyle",    description: "Impact of lifestyle on your menstrual cycle",         duration: "6-8 min", questions: 6 },
];

/* ── Helpers ── */
function getDayOfCycle(lastPeriodStart, cycleLength = 28) {
  if (!lastPeriodStart) return null;
  const start = new Date(lastPeriodStart);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((today - start) / 86400000) + 1;
  return diff > 0 && diff <= cycleLength * 2 ? diff : null;
}

function formatDateLong(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

/* ── CycleBot tool cards ── */
const TOOL_CARDS = [
  { key: "dashboard", icon: <Home size={28} />, iconBg: "#fce7f3", iconColor: "#db2777", title: "Dashboard",  description: "Cycle health overview & predictions"   },
  { key: "data",      icon: <BarChart3 size={28} />, iconBg: "#dbeafe", iconColor: "#2563eb", title: "Data View",  description: "Alerts, cycle history & search"        },
  { key: "chat",      icon: <MessageCircle size={28} />, iconBg: "#f5f3ff", iconColor: "#7c3aed", title: "AI Chat",    description: "AI wellness assistant"                 },
  { key: "journal",   icon: <BookOpen size={28} />, iconBg: "#fff7ed", iconColor: "#ea580c", title: "Journal",    description: "Track your wellbeing & mood"           },
];

/* ── kept for modal header lookup ── */
const ASSESS_VIEWS = [
  { key: "dashboard", icon: <Home size={28} />, iconBg: "#fce7f3", iconColor: "#db2777", title: "Dashboard",  description: "Cycle health overview & predictions"   },
  { key: "data",      icon: <BarChart3 size={28} />, iconBg: "#dbeafe", iconColor: "#2563eb", title: "Data View",  description: "Alerts, cycle history & search"        },
  { key: "chat",      icon: <MessageCircle size={28} />, iconBg: "#f5f3ff", iconColor: "#7c3aed", title: "AI Chat",    description: "AI wellness assistant"                 },
  { key: "flows",     icon: <Sprout size={28} />, iconBg: "#ecfdf5", iconColor: "#059669", title: "Assessment", description: "Guided assessment flows"               },
  { key: "journal",   icon: <BookOpen size={28} />, iconBg: "#fff7ed", iconColor: "#ea580c", title: "Journal",    description: "Track your wellbeing & mood"           },
];

/* ── Component ── */
export default function CycleTrackTab({
  cycleSettings,
  flows = [],
  flowsLoading = false,
  onStartFlow,
  onLogPeriod,
  /* ── CycleBot view props ── */
  dashboardData        = null,
  premenstrualWindow   = null,
  nestCycles           = [],
  searchResults        = null,
  onSearch,
  messages             = [],
  isTyping             = false,
  onSend,
  onNewConversation,
  chatLimit,
  journalSummary       = null,
  journalEntries       = [],
  journalEntriesLoading = false,
  onLogEntry,
  premenstrualReport   = null,
  premenstrualReportLoading = false,
  onRequestPMReport,
  activeFlowSession    = null,
  flowReport           = null,
  isGeneratingReport   = false,
  onAnswer,
  onCancelFlow,
}) {
  const { t } = useTranslation("cycle");
  const [selectedSymptoms,  setSelectedSymptoms]  = useState([]);
  const [activeAssessView,  setActiveAssessView]  = useState(null);
  const [selectedFlow,      setSelectedFlow]       = useState(null);
  const today      = new Date();
  const dayOfCycle = getDayOfCycle(cycleSettings?.lastPeriodStart, cycleSettings?.cycleLength);

  // Build translated SYMPTOMS
  const SYMPTOMS = useMemo(() => 
    SYMPTOMS_CONFIG.map(s => ({
      ...s,
      label: t(s.translationKey)
    }))
  , [t]);

  const toggleSymptom = (key) =>
    setSelectedSymptoms((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const assessments =
    flows.length > 0
      ? flows.map((f) => ({
          id:          f.id,
          emoji:       f.icon || "🧭",
          iconBg:      "#f5f3ff",
          iconColor:   "#7c3aed",
          title:       f.title,
          description: f.description,
          duration:    f.duration,
          questions:   f.questions_count || 0,
        }))
      : STATIC_ASSESSMENTS;

  const ACTION_BTNS = [
    { key: "period",   icon: <DropletIcon />,  label: t("cycleBot.trackTab.logPeriod"),   color: "#db2777", onClick: onLogPeriod || (() => {}) },
    { key: "mood",     icon: <HeartIcon />,    label: t("cycleBot.trackTab.logMood"),     color: "#7c3aed", onClick: () => {} },
    { key: "activity", icon: <ActivityIcon />, label: t("cycleBot.trackTab.logActivity"), color: "#2563eb", onClick: () => {} },
    { key: "note",     icon: <PlusIcon />,     label: t("cycleBot.trackTab.addNote"),     color: "#059669", onClick: () => {} },
  ];

  return (
    <div className="ctt">
      {/* ── Today's Log card ── */}
      <div className="ctt__card">
        <div className="ctt__log-header">
          <div>
            <h2 className="ctt__log-title">{t("cycleBot.trackTab.todaysLog")}</h2>
            <p className="ctt__log-date">{formatDateLong(today)}</p>
          </div>
          {dayOfCycle && (
            <span className="ctt__day-badge">Day {dayOfCycle} of cycle</span>
          )}
        </div>

        {/* 4 action buttons */}
        <div className="ctt__actions">
          {ACTION_BTNS.map((btn) => (
            <button key={btn.key} className="ctt__action-btn" onClick={btn.onClick}>
              <span className="ctt__action-icon" style={{ color: btn.color }}>
                {btn.icon}
              </span>
              <span className="ctt__action-label">{btn.label}</span>
            </button>
          ))}
        </div>

        {/* Track Symptoms */}
        <div className="ctt__symptoms">
          <h3 className="ctt__symptoms-title">{t("cycleBot.trackTab.trackSymptoms")}</h3>
          <div className="ctt__symptoms-grid">
            {SYMPTOMS.map((s) => {
              const isActive = selectedSymptoms.includes(s.key);
              return (
                <button
                  key={s.key}
                  className={`ctt__symptom-btn ${isActive ? "ctt__symptom-btn--active" : ""}`}
                  onClick={() => toggleSymptom(s.key)}
                  style={isActive ? {
                    borderColor: s.activeColor,
                    background:  s.activeBg,
                    color:       s.activeColor,
                  } : undefined}
                >
                  <span className="ctt__symptom-icon">
                    <SymptomIcon type={s.icon} />
                  </span>
                  <span className="ctt__symptom-label" style={isActive ? { color: s.activeColor } : undefined}>{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Guided Assessments card ── */}
      <div className="ctt__card">
        <div className="ctt__assess-header">
          <h2 className="ctt__assess-title">{t("cycleBot.trackTab.guidedAssessments")}</h2>
          <p className="ctt__assess-sub">{t("cycleBot.trackTab.guidedAssessmentsSub")}</p>
        </div>
        {flowsLoading ? (
          <p className="ctt__assess-loading">{t("cycleBot.trackTab.loadingAssessments")}</p>
        ) : (
          <div className="ctt__assess-grid">
            {(flows.length > 0 ? flows : STATIC_ASSESSMENTS).map((f) => (
              <div key={f.id} className="ctt__assess-card">
                <div
                  className="ctt__assess-icon"
                  style={{ background: f.iconBg || "#f5f3ff", color: f.iconColor || "#7c3aed" }}
                >
                  {f.icon || f.emoji || "🧭"}
                </div>
                <h3 className="ctt__assess-name">{flows.length > 0 ? f.title : t(`cycleBot.trackTab.assessments.${f.id}.title`, { defaultValue: f.title })}</h3>
                <p className="ctt__assess-desc">{flows.length > 0 ? f.description : t(`cycleBot.trackTab.assessments.${f.id}.description`, { defaultValue: f.description })}</p>
                {(f.duration || f.questions || f.questions_count) && (
                  <div className="ctt__assess-meta">
                    {f.duration && <span>⏱ {f.duration}</span>}
                    {(f.questions || f.questions_count) && (
                      <span>❓ {f.questions || f.questions_count} questions</span>
                    )}
                  </div>
                )}
                <button
                  className="ctt__assess-btn"
                  onClick={() => {
                    setSelectedFlow(f);
                    onStartFlow && onStartFlow(f.id);
                    setActiveAssessView("flows");
                  }}
                >
                  {t("cycleBot.trackTab.start")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── View Modal Popup ── */}
      {activeAssessView && (
        <div className="ctt__modal-overlay" onClick={() => { setActiveAssessView(null); setSelectedFlow(null); }}>
          <div className="ctt__modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="ctt__modal-header">
              <div className="ctt__modal-title-row">
                <span className="ctt__modal-emoji">
                  {activeAssessView === "flows"
                    ? (activeFlowSession?.flow?.icon || selectedFlow?.icon || selectedFlow?.emoji || "🧭")
                    : ASSESS_VIEWS.find((v) => v.key === activeAssessView)?.emoji}
                </span>
                <h2 className="ctt__modal-title">
                  {activeAssessView === "flows"
                    ? (activeFlowSession?.flow?.title || selectedFlow?.title || "Guided Assessment")
                    : ASSESS_VIEWS.find((v) => v.key === activeAssessView)?.title}
                </h2>
              </div>
              <button
                className="ctt__modal-close"
                onClick={() => { setActiveAssessView(null); setSelectedFlow(null); }}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6"  x2="6"  y2="18" />
                  <line x1="6"  y1="6"  x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="ctt__modal-body">
              {activeAssessView === "dashboard" && (
                <Dashboard
                  data={dashboardData}
                  premenstrualWindow={premenstrualWindow}
                  onGoJournal={() => setActiveAssessView("journal")}
                />
              )}
              {activeAssessView === "data" && (
                <DataView
                  alerts={dashboardData?.alerts || []}
                  cycles={nestCycles}
                  onSearch={onSearch}
                  searchResults={searchResults}
                />
              )}
              {activeAssessView === "chat" && (
                <ChatView
                  messages={messages}
                  onSend={onSend}
                  onNewConversation={onNewConversation}
                  isTyping={isTyping}
                  chatLimit={chatLimit}
                />
              )}
              {activeAssessView === "flows" && (
                <FlowsView
                  flows={flows}
                  onStartFlow={onStartFlow}
                  activeSession={activeFlowSession}
                  onAnswer={onAnswer}
                  onBack={() => {}}
                  onCancel={onCancelFlow}
                  report={flowReport}
                  onRestartFlow={() => {}}
                  onGoChat={() => setActiveAssessView("chat")}
                  isGeneratingReport={isGeneratingReport}
                />
              )}
              {activeAssessView === "journal" && (
                <JournalView
                  journalData={journalSummary}
                  entries={journalEntries}
                  entriesLoading={journalEntriesLoading}
                  onLogEntry={onLogEntry}
                  premenstrualWindow={premenstrualWindow}
                  pmReport={premenstrualReport}
                  pmReportLoading={premenstrualReportLoading}
                  onRequestPMReport={onRequestPMReport}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}